/**
 * Item Requisition — legacy ALPACA `Requisition_ListViewEntermode`.
 *
 * THE LEGACY FLOW (BTLIMS.Module/Controllers/ICM/RequisitionViewcontroller.cs):
 *
 *   Add Items -> uncommitted draft rows in the grid (Status 18, no RQID)
 *        v  edit OrderQty / DeliveryPriority / Department INLINE
 *        v  Submit
 *   validate every row -> duplicate guard -> mint RQID -> Status 0 PendingReview
 *        v  rows LEAVE this grid and appear in Requisition Review
 *
 * Two details drive the whole screen:
 *   - `Requisition.AfterConstruction` sets Status = PendingSubmission, which is
 *     18 — the LAST member of `TaskStatus`, not 0 (0 is PendingReview).
 *   - `RequisitionAddItem_Execute` only calls `CollectionSource.Add(...)`; it
 *     never commits, so a row the user adds and abandons is discarded.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { api } from '../../../api/client';
import { AppDataGrid, gridExportMatrix, type AppColumn } from '../../../components/AppDataGrid';
import { AppToolbar } from '../../../components/AppToolbar';
import { FormTitle } from '../../../components/FormTitle';
import { useNotify } from '../../../components/notifications';
import { useNavActions } from '../../../hooks/useNavActions';
import { useConfirm } from '../../../components/confirm';
import { fmtLocalDate } from '../../../utils/datetime';
import { AppDialog } from '../../../components/AppDialog';
import '../../backlog/backlog.css';
import '../../modules/SettingsDetailPage.css';
import { DateFilterSelect, monthsOf, type DateFilterId, defaultDateFilter } from './IcmDateFilter';
import { ICM_LIST_TAKE, icmTruncationNote } from '../icmPaging';
import './requisition.css';
import type { RequisitionPrefill } from './requisitionPrefill';

export interface RequisitionRow {
  oid: string;
  rqid: string | null;
  itemName: string;
  itemCode: string | null;
  description: string | null;
  vendorName: string | null;
  stockQty: number;
  orderQty: number;
  unitPrice: number | null;
  deliveryPriorityName: string | null;
  departmentName: string | null;
  errorlog: string | null;
  // Requisition Review columns (legacy Requisition_ListView_Review).
  catalog: string | null;          // Item.VendorCatName
  packUnits: string | null;        // Item.Unit -> Packageunits.option
  manufacturerName: string | null; // the REQUISITION's Manufacturer FK
  reviewedDate: string | null;
  reviewedByName: string | null;
  // FK ids, returned by the list SP so a PERSISTED draft can be posted back to
  // Submit with the values it already holds.
  itemId: string | null;
  vendorId: string | null;
  deliveryPriorityId: string | null;
  departmentId: string | null;
  statusLabel: string;
  requestedDate: string | null;
  requestedByName: string | null;
}

/** A grid row plus the ids Submit needs but the list SP does not return.
 *  `draft` marks a row that exists only in the browser (legacy's uncommitted
 *  CollectionSource row) — it has no database Oid yet. */
interface EditRow extends Omit<RequisitionRow, 'itemId'> {
  draft: boolean;
  itemId: string;
}

/** One row of the legacy "Items" picker popup (Items_LookupListView):
 *  Item | Vendor | Description | Catalog#. */
interface PickerItem {
  oid: string;
  itemName: string;
  defaultVendorName: string | null;
  defaultVendorId: string | null;
  description: string | null;
  catalog: string | null;
  unitPrice?: number | null;
  itemCode?: string | null;
  stockQty?: number | null;
}

interface Option { id: string; name: string }

const PICKER_PAGE_SIZE = 20;   // legacy popup's page size

/** Legacy `TaskStatus.PendingSubmission` — a DRAFT. It is the LAST member of
 *  ICM/Requisition.cs `TaskStatus`, so it is 18, NOT 0 (0 is PendingReview).
 *  Filtering on 0 made this screen list the REVIEW QUEUE instead of drafts
 *  (fixed 2026-09-24 — see AI_ICM_Requisition_StatusRemap_Migration.sql). */
const DRAFT_STATUS = '18';

/** Item Requisition HISTORY — EVERY requisition, with no status filter at all.
 *
 *  Legacy's History action (RequisitionViewcontroller.QHistoryReqView_Execute)
 *  builds a bare CollectionSource over Requisition and opens
 *  Requisition_ListView: the one status line it ever had is commented out and
 *  the view node declares no Criteria.
 *
 *  This was the enumerated list '0,1,2,3,4,5,6,7' — itself a fix for cancelled
 *  lines vanishing (user report 2026-09-29) — but an IN list is still not "no
 *  filter": it can never match a NULL Status, and it drops 8-17
 *  (Level1Pending..Level10Pending) if the lab ever turns on the multi-level
 *  approval chain. Measured on AlpacaLims_GoLive_Demo_20261005: 1 live row has a
 *  NULL Status, so the list returned 1,474 where legacy returns 1,475. */
const HISTORY_ALL_STATUSES = true;

export default function RequisitionListPage() {
  // MAC rights (hide, never grey): Add Items = Create, Submit / inline edit = Write, row X = Delete.
  const perm = useNavActions('icm-requisition');
  const notify = useNotify();
  const { confirm } = useConfirm();
  const location = useLocation();
  const navigate = useNavigate();

  const [rows, setRows] = useState<EditRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  // Legacy Requisition_ListViewEntermode shows ONLY drafts; History opens the
  // separate Requisition_ListView, which is read-only here.
  const [history, setHistory] = useState(false);

  /* ONE FILTER PER VIEW, not one filter reset on the toggle.
     Each view carries its own window -- a pending queue opens on ALL so nothing
     awaiting action is hidden, History opens on 3M -- and each remembers what
     the user last picked there.
     Resetting a single piece of state from an effect instead RACED: toggling to
     History committed (history=true, filter=ALL), fired a load, then the effect
     set 3M and fired a second one; the ALL response is the bigger payload, so it
     could land LAST and leave the grid showing 1,524 rows under a picker reading
     "3M". Deriving the value removes the second load entirely. */
  const [queueDateFilter, setQueueDateFilter] = useState<DateFilterId>(defaultDateFilter(false));
  const [histDateFilter,  setHistDateFilter]  = useState<DateFilterId>(defaultDateFilter(true));
  const dateFilter    = history ? histDateFilter : queueDateFilter;
  const setDateFilter = history ? setHistDateFilter : setQueueDateFilter;
  const [submitting, setSubmitting] = useState(false);

  // Inline-edit lookups (legacy edits these three columns in the grid itself).
  const [priorities, setPriorities] = useState<Option[]>([]);
  const [departments, setDepartments] = useState<Option[]>([]);

  // "Add Items" -> legacy Items picker popup
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerItems, setPickerItems] = useState<PickerItem[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');
  const [pickerSel, setPickerSel] = useState<Set<string>>(new Set());
  /** Rows ticked in HISTORY mode, which is what legacy's Re-Order acts on
   *  (ItemViewController.Reorder_Execute ~1376 reads View.SelectedObjects and
   *  opens the entry grid carrying those items). The draft grid stays
   *  unselectable, as legacy's entry view is. */
  const [selected, setSelected] = useState<Set<string | number>>(new Set());

  const draftSeq = useRef(0);

  /* STOCK ALERT HAND-OFF (legacy AlertViewControllercs.OrderAction_Execute ->
     RequisitionViewcontroller :315-343). Stock Alert's per-row "Requisition"
     link opens THIS list carrying one item in `location.state`; legacy then
     CreateObject()s an uncommitted Requisition from it and adds it to the
     grid's CollectionSource. Here that is literally the same draft row the
     Items picker builds, so the two entry points cannot drift.

     Legacy clears its session bag (`fromStockAlert = false; SelectedItem =
     null`) immediately after adding the row, for the obvious reason: the view
     is re-activated on every postback and would otherwise add the item again.
     `navigate(replace)` with an empty state is the same guard -- without it a
     Refresh, a back-navigation or an unrelated re-render would re-add the row.
     `addedPrefill` covers the re-render inside this mount, before the router
     state actually clears.

     The row is PARKED in a ref rather than pushed straight into `rows`, because
     `load()` runs on the same mount and ends with `setRows(serverRows)` -- it
     would wipe anything this effect had already put there (it did: the first
     build of this landed on the right screen with an empty grid). `load` picks
     the parked row up and prepends it to the fetched set, which also means a
     later Refresh behaves like legacy's ObjectSpace.Refresh and drops the
     uncommitted draft, exactly as it drops one added through the Items picker. */
  const addedPrefill = useRef(false);
  const pendingPrefill = useRef<EditRow | null>(null);
  useEffect(() => {
    const prefill = (location.state as { prefill?: RequisitionPrefill } | null)?.prefill;
    if (!prefill || addedPrefill.current) return;
    addedPrefill.current = true;
    setHistory(false);                     // the draft grid is where a draft goes
    pendingPrefill.current = {
      oid: `draft-${++draftSeq.current}`,
      draft: true,
      rqid: null,
      itemId: prefill.itemId,
      itemName: prefill.itemName ?? '',
      itemCode: prefill.itemCode ?? null,
      description: prefill.description ?? null,
      vendorId: prefill.vendorId ?? null,
      vendorName: prefill.vendorName ?? null,
      stockQty: prefill.stockQty ?? 0,
      orderQty: 1,                         // legacy AfterConstruction: OrderQty = 1
      unitPrice: prefill.unitPrice ?? 0,
      deliveryPriorityId: null,
      deliveryPriorityName: null,
      departmentId: null,
      departmentName: null,
      errorlog: null,
      catalog: prefill.catalog ?? null,
      packUnits: null,
      manufacturerName: prefill.manufacturerName ?? null,
      reviewedDate: null,
      reviewedByName: null,
      statusLabel: 'Pending Submission',
      requestedDate: new Date().toISOString(),
      requestedByName: null,
    };
    notify.success(`1 item(s) added.`);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    const t0 = performance.now();
    try {
      const res = await api.get('/api/v1/requisitions',
        { params: {
            /* History also hides requisitions whose ITEM has been retired --
               legacy's second criteria key on this view ([Item.RetireDate] IS NULL
               OR >= now, RequisitionViewcontroller :1159). Requisition Review does
               not carry it, so it is sent from here only. */
            /* ...and, on History, legacy's own-requisitions rule: a user who is
               not a manager and is neither Administrator nor Service sees only
               what they raised (RequisitionViewcontroller :1152-1158). This flag
               only says the VIEW has the rule -- the API decides who it applies
               to, from the signed-in user. */
            ...(history ? { allStatuses: HISTORY_ALL_STATUSES, excludeRetiredItems: true,
                            restrictToOwnRequisitions: true }
                        : { status: DRAFT_STATUS }),
            /* No window on the DRAFT QUEUE -- it has no chip, so it must not
               carry a hidden filter either. History sends the chip's months. */
            ...(history ? { months: monthsOf(dateFilter) } : {}),
            take: ICM_LIST_TAKE } });
      const body = res.data as { items?: RequisitionRow[]; total?: number };
      const items = body.items ?? [];
      const fetched: EditRow[] = items.map(r => ({ ...r, draft: false, itemId: r.itemId ?? '' }));
      /* A Stock Alert hand-off parked a draft row before this fetch started
         (see the prefill effect above) -- prepend it, once. */
      const parked = pendingPrefill.current;
      pendingPrefill.current = null;
      setRows(parked ? [parked, ...fetched] : fetched);
      /* No `take` was sent here at all, so the API default of 50 applied and
         History showed 50 of 1,524 (owner report 2026-10-01). */
      const note = icmTruncationNote(items.length, body.total);
      if (note) notify.info(note);
    } catch {
      setError('Failed to load requisitions.');
      setRows([]);
    } finally {
      setLoadMs(performance.now() - t0);
      setLoading(false);
    }
  }, [history, dateFilter, notify]);

  useEffect(() => { void load(); }, [load]);

  /** The TOP-BAR title follows the view, as legacy's does — its History action
   *  opens a different ListView with its own caption. Uses the SHARED
   *  `ai:topbar-title` override AppShell already listens for (the same one the
   *  QC / Prep Batch pages use), so no new plumbing and nothing to drift.
   *  Cleared on unmount, and set back to the plain name when History is off. */
  useEffect(() => {
    window.dispatchEvent(new CustomEvent('ai:topbar-title',
      { detail: history ? 'Item Requisition History' : null }));
    return () => { window.dispatchEvent(new CustomEvent('ai:topbar-title', { detail: null })); };
  }, [history]);

  // Inline-edit option lists — loaded once, only for the draft (editable) view.
  useEffect(() => {
    if (history) return;
    let cancelled = false;
    void (async () => {
      try {
        const [dp, dept] = await Promise.all([
          api.get('/api/v1/delivery-priorities'),
          api.get('/api/v1/system/departments'),
        ]);
        if (cancelled) return;
        // The two lookups do NOT share a response shape: /delivery-priorities
        // returns a bare array, /system/departments returns the standard
        // envelope {success, data:[...]} (§6.10). Handling only `items` left
        // Department with no options at all, so the cell read "No matches".
        const norm = (d: unknown): Option[] => {
          const box = d as { data?: unknown[]; items?: unknown[] } | undefined;
          const arr = Array.isArray(d) ? d : (box?.data ?? box?.items ?? []);
          return (arr as Record<string, unknown>[]).map(o => ({
            id: String(o.oid ?? o.id ?? ''),
            name: String(o.name ?? o.deliveryPriorityName ?? o.departmentName ?? ''),
          })).filter(o => o.id && o.name);
        };
        setPriorities(norm(dp.data));
        setDepartments(norm(dept.data));
      } catch {
        // Non-fatal: the cells fall back to read-only rather than breaking the grid.
      }
    })();
    return () => { cancelled = true; };
  }, [history]);

  const filtered = useMemo(() => {
    const t = search.trim().toLowerCase();
    if (!t) return rows;
    return rows.filter(r =>
      (r.rqid ?? '').toLowerCase().includes(t) ||
      r.itemName.toLowerCase().includes(t) ||
      (r.vendorName ?? '').toLowerCase().includes(t) ||
      (r.departmentName ?? '').toLowerCase().includes(t) ||
      (r.requestedByName ?? '').toLowerCase().includes(t)
    );
  }, [rows, search]);

  /** Ticked rows export on their own; with nothing ticked the whole list does —
   *  the same convention as the other ICM grids, so the History tick column is
   *  not decoration now that Re-Order no longer consumes it. */
  const exportRows = selected.size ? filtered.filter(r => selected.has(r.oid)) : filtered;

  /** Commit one inline cell edit into local state. Nothing is persisted here —
   *  legacy's batch grid holds every edit until Submit. */
  const commitCell = useCallback((row: EditRow, col: AppColumn<EditRow>, value: string) => {
    setRows(prev => prev.map(r => {
      if (r.oid !== row.oid) return r;
      if (col.field === 'orderQty') {
        const n = Number(value);
        return { ...r, orderQty: Number.isFinite(n) ? n : r.orderQty, errorlog: null };
      }
      if (col.field === 'deliveryPriorityName') {
        const hit = priorities.find(o => o.name === value);
        return { ...r, deliveryPriorityName: hit?.name ?? null, deliveryPriorityId: hit?.id ?? null, errorlog: null };
      }
      if (col.field === 'departmentName') {
        const hit = departments.find(o => o.name === value);
        return { ...r, departmentName: hit?.name ?? null, departmentId: hit?.id ?? null, errorlog: null };
      }
      return r;
    }));
  }, [priorities, departments]);

  /** Legacy `Requisitiondelete` — removes ONE row (the red X). A buffered draft
   *  never reached the database, so it is simply dropped from state. */
  const deleteRow = useCallback(async (row: EditRow) => {
    if (!(await confirm({
      title: 'Delete',
      message: `Delete the requisition for "${row.itemName}"?`,
      tone: 'danger',
      confirmLabel: 'Delete',
    }))) return;

    if (row.draft) {
      setRows(prev => prev.filter(r => r.oid !== row.oid));
      notify.success('Deleted successfully.');
      return;
    }
    try {
      await api.delete(`/api/v1/requisitions/${row.oid}`);
      await load();
      notify.success('Deleted successfully.');
    } catch {
      notify.error('Delete failed.');
    }
  }, [confirm, notify, load]);

  // Columns mirror the LIVE ALPACA GoLive grid exactly: Item / ItemCode /
  // Description / Vendor / StockQty / OrderQty / DeliveryPriority / Department /
  // Errorlog / RequestedDate / Status, with the legacy captions verbatim.
  // OrderQty, DeliveryPriority and Department are INLINE-EDITABLE on the draft
  // view (InlineEditMode="Batch"); History is read-only, as in legacy.
  const editable = !history;
  const cols: AppColumn<EditRow>[] = [
    ...(editable && perm.delete ? [{
      /* Legacy `Requisitiondelete`: Category="RecordEdit" + RequireSingleObject,
         which XAF draws as the red X inline row command in the leading column —
         which is why the view sets ShowSelectionColumn="False". The icon is
         DevExpress `Action_Delete` (a bold red X, not a trash can). Legacy sets
         ConfirmationMessage=null; AI confirms first, per standing rule 7. */
      field: 'oid', header: '', width: 38, sortable: false, filterable: false,
      noExport: true, text: () => '',
      render: (r: EditRow) => (
        <button
          type="button"
          className="rq-row-del"
          title="Delete"
          aria-label={`Delete requisition for ${r.itemName}`}
          onClick={e => { e.stopPropagation(); void deleteRow(r); }}
        ><i className="ti ti-x" aria-hidden="true" /></button>
      ),
    } as AppColumn<EditRow>] : []),
    ...(history ? [] : [
      { field: 'itemName',    header: 'Item',        width: 170, text: (r: EditRow) => r.itemName },
      { field: 'itemCode',    header: 'ItemCode',    width: 120, text: (r: EditRow) => r.itemCode ?? '', render: (r: EditRow) => r.itemCode ?? '' },
      { field: 'description', header: 'Description', width: 200, text: (r: EditRow) => r.description ?? '', render: (r: EditRow) => r.description ?? '' },
      { field: 'vendorName',  header: 'Vendor',      width: 160, text: (r: EditRow) => r.vendorName ?? '', render: (r: EditRow) => r.vendorName ?? '' },
      { field: 'stockQty',    header: 'StockQty',    width: 90, type: 'number', text: (r: EditRow) => String(r.stockQty), value: (r: EditRow) => r.stockQty },
    ] as AppColumn<EditRow>[]),
    /* HISTORY leads with Date . Item . Description . Catalog# . Vendor, which is
       the legacy order (BTLIMS.Web/Model.xafml Requisition_ListView: RequestedDate
       captioned "Date" at Index 0, DisplayFormat {0:d} = date only). StockQty
       follows OrderQty there, not Vendor. */
    ...(history ? [
      { field: 'requestedDate', header: 'Date',        width: 110,
        text: (r: EditRow) => fmtLocalDate(r.requestedDate) ?? '',
        render: (r: EditRow) => fmtLocalDate(r.requestedDate) ?? '' },
      { field: 'itemName',    header: 'Item',        width: 170, text: (r: EditRow) => r.itemName },
      { field: 'description', header: 'Description', width: 200, text: (r: EditRow) => r.description ?? '', render: (r: EditRow) => r.description ?? '' },
      { field: 'catalog',     header: 'Catalog#',    width: 120, text: (r: EditRow) => r.catalog ?? '', render: (r: EditRow) => r.catalog ?? '' },
      { field: 'vendorName',  header: 'Vendor',      width: 160, text: (r: EditRow) => r.vendorName ?? '', render: (r: EditRow) => r.vendorName ?? '' },
    ] as AppColumn<EditRow>[] : []),
    {
      field: 'orderQty', header: 'OrderQty', width: 90, type: 'number',
      text: r => String(r.orderQty), value: r => r.orderQty,
      ...(editable && perm.write ? { edit: { type: 'number' as const, requiredEmpty: (r: EditRow) => !r.orderQty } } : {}),
    },
    ...(history ? [
      { field: 'stockQty',  header: 'StockQty',  width: 90, type: 'number',
        text: (r: EditRow) => String(r.stockQty), value: (r: EditRow) => r.stockQty },
      { field: 'packUnits', header: 'PackUnits', width: 95,
        text: (r: EditRow) => r.packUnits ?? '', render: (r: EditRow) => r.packUnits ?? '' },
    ] as AppColumn<EditRow>[] : []),
    {
      field: 'deliveryPriorityName', header: 'DeliveryPriority', width: 130,
      text: r => r.deliveryPriorityName ?? '', render: r => r.deliveryPriorityName ?? '',
      ...(editable && perm.write ? { edit: { type: 'combo' as const, options: priorities.map(o => o.name), requiredEmpty: (r: EditRow) => !r.deliveryPriorityName } } : {}),
    },
    {
      field: 'departmentName', header: 'Department', width: 120,
      text: r => r.departmentName ?? '', render: r => r.departmentName ?? '',
      ...(editable && perm.write ? { edit: { type: 'combo' as const, options: departments.map(o => o.name), requiredEmpty: (r: EditRow) => !r.departmentName } } : {}),
    },
    /* Errorlog and RequestedDate belong to the DRAFT grid only. Legacy hides
       both on the History view (Requisition_ListView: Errorlog Index="-1", and
       RequestedDate is re-captioned "Date" and moved to the front), so History
       showed a permanently blank Errorlog and the same date twice. */
    ...(history ? [] : [
      {
        /* Legacy writes the per-row failure here on Submit and paints the cell
           OrangeRed (grid Init script, RequisitionViewcontroller ~812). */
        field: 'errorlog', header: 'Errorlog', width: 160,
        text: (r: EditRow) => r.errorlog ?? '',
        cellClass: (r: EditRow) => (r.errorlog ? 'rq-cell-error' : undefined),
        render: (r: EditRow) => r.errorlog ?? '',
      },
      { field: 'requestedDate', header: 'RequestedDate', width: 120,
        text: (r: EditRow) => fmtLocalDate(r.requestedDate) ?? '',
        render: (r: EditRow) => fmtLocalDate(r.requestedDate) ?? '' },
    ] as AppColumn<EditRow>[]),
    ...(history ? [
      { field: 'requestedByName', header: 'RequestedBy', width: 130,
        text: (r: EditRow) => r.requestedByName ?? '', render: (r: EditRow) => r.requestedByName ?? '' },
    ] as AppColumn<EditRow>[] : []),
    {
      /* On a row that FAILED validation the Status cell goes OrangeRed, as the
         GoLive grid shows it (owner decision 2026-09-26: copy the screenshot).
         Legacy paints it from RequisitionViewcontroller's client-side Init
         script (~810-835), which splits the Errorlog on "and" and sets
         `cells[11].style.backgroundColor='OrangeRed'` — index 11 is the STATUS
         column in the current layout. The script's own captions say it means
         DeliveryPriority / Department, so the indices have drifted from the
         column order; this reproduces what the running app DRAWS, which is what
         was asked for. Without an Errorlog the cell keeps the shared status
         pill, so every other row still reads like the rest of the app. */
      field: 'statusLabel', header: 'Status', width: 140, type: 'status',
      text: r => r.statusLabel,
      cellClass: r => (r.errorlog ? 'rq-cell-status-error' : undefined),
    },
  ];

  /** Legacy RequisitionSave ("Submit"): the grid IS the batch, so this commits
   *  EVERY row in one call. The server validates all rows, applies the
   *  duplicate-item guard, and writes nothing unless all pass — so a partial
   *  submit is impossible. Failures come back per row and land in Errorlog. */
  async function submitAll() {
    if (rows.length === 0) { notify.warning('Select data.'); return; }
    if (!(await confirm({
      title: 'Submit',
      message: rows.length === 1
        ? 'Do you want to save the Item?'
        : `Do you want to save all ${rows.length} items?`,
      confirmLabel: 'Yes',
      cancelLabel: 'No',
    }))) return;

    const payload = rows.map(r => ({
      oid: r.draft ? null : r.oid,
      itemId: r.itemId,
      vendorId: r.vendorId,
      manufacturerId: null,
      catalog: null,
      orderQty: r.orderQty,
      unitPrice: r.unitPrice ?? 0,
      deliveryPriorityId: r.deliveryPriorityId,
      departmentId: r.departmentId,
      projectId: null,
      comment: null,
    }));

    setSubmitting(true);
    try {
      await api.post('/api/v1/requisitions/submit-batch', payload);
      await load();
      notify.success('Submit completed successfully.');
    } catch (e) {
      // 400 carries one result per row; map the Errorlog back onto the grid so
      // the user sees exactly which row failed and why (legacy parity).
      const data = (e as { response?: { data?: unknown } })?.response?.data;
      const results = Array.isArray(data)
        ? data as { rowIndex: number; errorlog: string | null }[]
        : null;
      if (results) {
        setRows(prev => prev.map((r, i) => ({
          ...r,
          errorlog: results.find(x => x.rowIndex === i)?.errorlog ?? null,
        })));
        const first = results.find(x => x.errorlog)?.errorlog;
        notify.error(first ?? 'Save unsuccessful, refer the errorlog column.');
      } else {
        notify.error('Save unsuccessful, refer the errorlog column.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  /** Legacy: RequisitionAddItem ("Add Items") opens Items_LookupListView. */
  async function openPicker() {
    setPickerOpen(true);
    setPickerSel(new Set());
    setPickerSearch('');
    if (pickerItems.length > 0) return;   // catalog is static; fetch once
    setPickerLoading(true);
    try {
      const res = await api.get('/api/v1/requisitions/items', { params: { pickable: true } });
      const data = res.data as { items?: PickerItem[] } | PickerItem[];
      setPickerItems(Array.isArray(data) ? data : (data.items ?? []));
    } catch {
      notify.error('Failed to load items.');
      setPickerItems([]);
    } finally {
      setPickerLoading(false);
    }
  }

  const pickerFiltered = useMemo(() => {
    /* G5 hides an item already added and not yet saved, matched BY NAME, so every item
       sharing that name goes too (RequisitionViewcontroller :442, objreq.Items). */
    const added = new Set(rows.filter(r => r.draft).map(r => r.itemName));
    const offered = pickerItems.filter(i => !added.has(i.itemName));
    const t = pickerSearch.trim().toLowerCase();
    if (!t) return offered;
    return offered.filter(i =>
      i.itemName.toLowerCase().includes(t) ||
      (i.defaultVendorName ?? '').toLowerCase().includes(t) ||
      (i.description ?? '').toLowerCase().includes(t) ||
      (i.catalog ?? '').toLowerCase().includes(t));
  }, [pickerItems, pickerSearch, rows]);

  /** Picker columns — the legacy popup's four: Item | Vendor | Description | Catalog#. */
  const pickerCols: AppColumn<PickerItem>[] = [
    { field: 'itemName',          header: 'Item',        width: 240, text: i => i.itemName },
    { field: 'defaultVendorName', header: 'Vendor',      width: 150, text: i => i.defaultVendorName ?? '', render: i => i.defaultVendorName ?? '' },
    { field: 'description',       header: 'Description', width: 280, text: i => i.description ?? '',       render: i => i.description ?? '' },
    { field: 'catalog',           header: 'Catalog#',    width: 140, text: i => i.catalog ?? '',           render: i => i.catalog ?? '' },
  ];

  /** Legacy OK: adds one row per ticked item to the GRID ONLY. Nothing is
   *  written — `RequisitionAddItem_Execute` just calls CollectionSource.Add, so
   *  an abandoned row is discarded. AI used to POST here, which created the
   *  requisition immediately (and, under the old status map, born already
   *  submitted for review). */
  /* RE-ORDER was removed on 2026-09-26 (owner report). It is not an Item
     Requisition action at all: legacy declares it on ItemViewController
     (Reorder_Execute ~1376), i.e. the ITEMS master, and nothing targets it at
     Requisition_ListView. It had been added here the day before on the
     assumption that History was where it belonged. */

  function confirmPicker() {
    if (pickerSel.size === 0) { notify.warning('Select data.'); return; }
    const chosen = pickerItems.filter(i => pickerSel.has(i.oid));
    const now = new Date().toISOString();
    const added: EditRow[] = chosen.map(it => ({
      oid: `draft-${++draftSeq.current}`,
      draft: true,
      rqid: null,
      itemId: it.oid,
      itemName: it.itemName,
      itemCode: it.itemCode ?? null,
      description: it.description ?? null,
      // Legacy fills Vendor from the picked Item's own vendor, which Ordering
      // Items later groups by — without it the row lands in "(No vendor)".
      vendorId: it.defaultVendorId ?? null,
      vendorName: it.defaultVendorName ?? null,
      stockQty: it.stockQty ?? 0,
      orderQty: 1,                        // legacy AfterConstruction: OrderQty = 1
      unitPrice: it.unitPrice ?? 0,
      deliveryPriorityId: null,
      deliveryPriorityName: null,
      departmentId: null,
      departmentName: null,
      errorlog: null,
      catalog: it.catalog ?? null,
      packUnits: null,             // a draft has no pack unit until it is saved
      manufacturerName: null,      // legacy leaves a draft's manufacturer unset
      reviewedDate: null,          // a draft has not been reviewed
      reviewedByName: null,
      statusLabel: 'Pending Submission',
      requestedDate: now,
      requestedByName: null,
    }));
    setRows(prev => [...added, ...prev]);
    setPickerOpen(false);
    notify.success(`${added.length} item(s) added.`);
  }

  return (
    <div className="bl-page">
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>ICM Operations</span><i className="ti ti-chevron-right" />
            {history ? (
              <>
                {/* The way BACK, now that History drops the "Pending" button:
                    legacy returns to the entry grid through its own navigation,
                    and AI's breadcrumb is that navigation. Same pattern as the
                    Receiving Items / Item Distribution detail breadcrumbs. */}
                <span onClick={() => { setSelected(new Set()); setHistory(false); }}
                      style={{ cursor: 'pointer' }}>Item Requisition</span>
                <i className="ti ti-chevron-right" />
                <span className="dp-bc-active">Item Requisition History</span>
              </>
            ) : (
              <span className="dp-bc-active">Item Requisition</span>
            )}
          </div>
          <div className="dp-title-row">
            {/* The heading follows the VIEW, as legacy's does — its History
                action opens a different ListView with its own caption. pageId
                still supplies the nav icon and colour (navMeta, S18.6), so the
                two cannot drift. */}
            <FormTitle
              pageId="icm-requisitions" auditFormName="Item Requisition"
              title={history ? 'Item Requisition History' : 'Item Requisition'}
              loadMs={loadMs}
              loading={loading}
            />
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={
              <>
                {/* ENTRY-GRID ACTIONS ONLY. Legacy targets Submit
                    (RequisitionSave), Add Items (RequisitionAddItem) and History
                    (HistoryReqView) at Requisition_ListViewEntermode alone — none
                    of them appears on the History view, which is a different
                    ListView. They used to render here disabled, which read as
                    "available but broken". Re-Order is gone outright: it is an
                    action on the ITEMS master (ItemViewController.Reorder), not on
                    Item Requisition, so it never belonged on this screen. */}
                {!history && (
                  <>
                    {perm.write && <button className="ca-btn" onClick={submitAll}
                      disabled={rows.length === 0 || submitting}>
                      <i className="ti ti-device-floppy" /> {submitting ? 'Submitting…' : 'Submit'}
                    </button>}
                    <button className="ca-btn" style={{ marginLeft: 6 }}
                      onClick={() => { setSelected(new Set()); setHistory(true); }}
                      title="Show submitted requisitions">
                      <i className="ti ti-history" /> History
                    </button>
                  </>
                )}
                {/* HISTORY ONLY. Legacy's Item Requisition bar is
                    New · Submit · History · Refresh · Export to -- no Date Filter
                    (the same bar that makes this the one form passing
                    `AppToolbar newFirst`). RequisitionDateFilter.TargetViewId :83
                    names only Requisition_ListView_ViewMode and _Review, so the
                    draft queue never carries the chip; its History view does
                    (owner screenshot 2026-10-05 shows "Date Filter 6M" there).
                    Removed from the queue on owner request 2026-10-06. */}
                {history && <DateFilterSelect value={dateFilter} onChange={setDateFilter} />}
              </>
            }
            onNew={history || !perm.create ? undefined : openPicker}
            /* Legacy flips this caption on the grid's row count
               (RequisitionViewcontroller ~509): rows present = "Add Items",
               empty = "New". newFirst puts New ahead of Submit/History to match
               the legacy order (the one sanctioned <AppToolbar> order opt-in). */
            newLabel={rows.length > 0 ? 'Add Items' : 'New'}
            newFirst
            onRefresh={load}
            refreshing={loading}
            exportData={{ filename: history ? 'requisition-history' : 'requisitions', ...gridExportMatrix(exportRows, cols) }}
          />
        </div>
        <div className="list-search-row">
          <div className="list-search">
            <span className="tb-i">🔍</span>
            <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search RQID, item, vendor…" />
            {search && <span className="list-search-clear" onClick={() => setSearch('')}>×</span>}
          </div>
        </div>
        <div className="bl-card bl-card-flex">
          {error ? (
            <div className="bl-err"><i className="ti ti-alert-circle" /> {error}</div>
          ) : (
            <AppDataGrid<EditRow>
              rows={filtered}
              columns={cols}
              getRowId={r => r.oid}
              loading={loading}
              loadMs={loadMs}
              /* Selection is on in HISTORY only, which is what Re-Order acts on
                 (legacy's Requisition_ListView sets no ShowSelectionColumn, so
                 XAF shows the tick column there). The DRAFT entry grid stays
                 unselectable — legacy's entry view is. */
              selectable={history}
              selectedIds={selected}
              onSelectionChange={setSelected}
              /* Previously: legacy ShowSelectionColumn="False" — the leading
                 column is the red X row-delete instead. */
              defaultPageSize={20}
              onCellCommit={commitCell}
              /* NO ROW-CLICK DRILL-DOWN — legacy has none on this form
                 (verified 2026-09-26). Requisition_DetailView exists in the
                 model, but nothing on Item Requisition opens it: the draft grid
                 (Requisition_ListViewEntermode) sets InlineEditMode="Batch" with
                 no MasterDetailView and no DetailViewID, and the History view
                 (Requisition_ListView) sets AllowEdit="False" and explicitly
                 hides the Edit action. The one view wired to that DetailView is
                 Ordering Items History
                 (Requisition_ListView_Purchaseorder_ViewMode,
                 MasterDetailView="Requisition_DetailView") — a different screen.
                 So Item Requisition is grid-only: add rows, edit the cells,
                 Submit. Stock Alert's "Requisition" link lands HERE too (see the
                 prefill effect above), exactly as legacy's OrderAction opens
                 Requisition_ListViewEntermode -- it does NOT open a detail form.
                 RequisitionDetailPage is still routed at /m/icm-requisition/new
                 but nothing links to it since 2026-10-06. */
              emptyText={history ? 'No requisitions found.' : 'No data to display'}
            />
          )}
        </div>
      </div>

      {/* Legacy "Items" picker popup (Items_LookupListView) behind Add Items */}
      <AppDialog
        open={pickerOpen}
        title="Items"
        icon="ti-package"
        width={900}
        onClose={() => setPickerOpen(false)}
        footer={
          <>
            <button className="ca-btn ca-btn-ghost" onClick={() => setPickerOpen(false)}>Cancel</button>
            <button className="ca-btn" onClick={confirmPicker}>OK</button>
          </>
        }
      >
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
          <input
            className="sd-input"
            style={{ width: 260 }}
            type="text"
            value={pickerSearch}
            placeholder="Text to search..."
            onChange={e => setPickerSearch(e.target.value)}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <AppDataGrid<PickerItem>
            rows={pickerFiltered}
            columns={pickerCols}
            getRowId={i => i.oid}
            loading={pickerLoading}
            loadMs={null}
            defaultPageSize={PICKER_PAGE_SIZE}
            selectable
            selectedIds={pickerSel as Set<string | number>}
            onSelectionChange={ids => setPickerSel(new Set(Array.from(ids).map(String)))}
            emptyText="No items found."
          />
        </div>
      </AppDialog>
    </div>
  );
}
